import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  filter,
  mergeMap,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  share,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { errMessage } from '@ui';
import { ContactsActions } from '../state/contacts/contacts.actions';
import { contactsFeature } from '../state/contacts/contacts.feature';
import { ContactsService } from '../../services/contacts.service';
import { SharedActions } from '../state/shared/shared.actions';

export const startLoadingContacts$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ContactsActions.startLoadingContacts),
      withLatestFrom(
        store.select(contactsFeature.selectListLoaded),
        store.select(contactsFeature.selectIsLoading),
        store.select(contactsFeature.selectSearch),
      ),
      filter(([, listLoaded, isLoading]) => !listLoaded && !isLoading),
      exhaustMap(([, , , search]) => [
        ContactsActions.setIsLoading({ isLoading: true }),
        ContactsActions.loadContacts({ search, lastId: null, append: false }),
      ]),
    ),
  { functional: true },
);

export const loadContacts$ = createEffect(
  (actions$ = inject(Actions), service = inject(ContactsService)) =>
    actions$.pipe(
      ofType(ContactsActions.loadContacts),
      switchMap(({ search, lastId, append }) =>
        service.getContacts({ search, lastId }).pipe(
          switchMap((contacts) => [
            ContactsActions.loadContactsSuccess({ contacts, append }),
            ContactsActions.setIsLoading({ isLoading: false }),
            ContactsActions.setIsLoadingMore({ isLoadingMore: false }),
          ]),
          catchError((response: HttpErrorResponse) => [
            ContactsActions.loadContactsFail(),
            ContactsActions.setIsLoading({ isLoading: false }),
            ContactsActions.setIsLoadingMore({ isLoadingMore: false }),
            SharedActions.showMessage({ error: errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const searchContacts$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactsActions.searchContacts),
      debounceTime(300),
      distinctUntilChanged((previous, current) => previous.search === current.search),
      switchMap(({ search }) => [
        ContactsActions.setIsLoading({ isLoading: true }),
        ContactsActions.loadContacts({ search, lastId: null, append: false }),
      ]),
    ),
  { functional: true },
);

export const resetContactsSearch$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactsActions.resetContactsSearch),
      switchMap(() => [
        ContactsActions.setIsLoading({ isLoading: true }),
        ContactsActions.loadContacts({ search: '', lastId: null, append: false }),
      ]),
    ),
  { functional: true },
);

export const reloadContacts$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ContactsActions.reloadContacts),
      withLatestFrom(store.select(contactsFeature.selectSearch)),
      switchMap(([, search]) => [
        ContactsActions.setIsLoading({ isLoading: true }),
        ContactsActions.loadContacts({ search, lastId: null, append: false }),
      ]),
    ),
  { functional: true },
);

export const loadMoreContacts$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ContactsActions.loadMoreContacts),
      withLatestFrom(
        store.select(contactsFeature.selectIsLastPage),
        store.select(contactsFeature.selectIsLoadingMore),
        store.select(contactsFeature.selectSearch),
        store.select(contactsFeature.selectLastId),
      ),
      filter(([, isLastPage, isLoadingMore, , lastId]) =>
        !isLastPage && !isLoadingMore && Boolean(lastId),
      ),
      exhaustMap(([, , , search, lastId]) => [
        ContactsActions.setIsLoadingMore({ isLoadingMore: true }),
        ContactsActions.loadContacts({ search, lastId, append: true }),
      ]),
    ),
  { functional: true },
);

export const loadMemberStats$ = createEffect(
  (actions$ = inject(Actions), service = inject(ContactsService)) =>
    actions$.pipe(
      ofType(ContactsActions.loadMemberStats),
      switchMap(() =>
        service.getStats().pipe(
          switchMap((stats) => [
            ContactsActions.loadMemberStatsSuccess({ stats }),
          ]),
          catchError(() => [ContactsActions.loadMemberStatsFail()]),
        ),
      ),
    ),
  { functional: true },
);

export const startImportContacts$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactsActions.startImportContacts),
      exhaustMap(({ file }) => [
        ContactsActions.setIsSaving({ isSaving: true }),
        ContactsActions.importContacts({ file }),
      ]),
    ),
  { functional: true },
);

export const importContacts$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactsActions.importContacts),
      mergeMap(({ file }) =>
        service.getImportUploadUrl(file.name).pipe(
          mergeMap(({ url, filePath }) =>
            service
              .uploadImportFile(url, file)
              .pipe(mergeMap(() => service.processImport(filePath))),
          ),
          switchMap((result) => [
            ContactsActions.importContactsSuccess({ result }),
            ContactsActions.setIsSaving({ isSaving: false }),
            ContactsActions.reloadContacts(),
            ContactsActions.loadMemberStats(),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACTS_IMPORTED', {
                count: result.created,
              }) as string,
            }),
            ...(result.failed
              ? [
                  SharedActions.showMessage({
                    warning: translate.instant(
                      'TOASTS.CONTACTS_IMPORT_PARTIAL',
                      { count: result.failed },
                    ) as string,
                  }),
                ]
              : []),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactsActions.setIsSaving({ isSaving: false }),
            ContactsActions.importContactsFail(),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startCreateContact$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactsActions.startCreateContact),
      exhaustMap(({ create }) => [
        ContactsActions.setIsSaving({ isSaving: true }),
        ContactsActions.createContact({ create }),
      ]),
      share(),
    ),
  { functional: true },
);

export const createContact$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactsActions.createContact),
      mergeMap(({ create }) =>
        service.create(create).pipe(
          switchMap((contact) => [
            ContactsActions.createContactSuccess({ contact }),
            ContactsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_CREATED') as string,
            }),
            ContactsActions.loadMemberStats(),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactsActions.setIsSaving({ isSaving: false }),
            ContactsActions.createContactFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startUpdateContact$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactsActions.startUpdateContact),
      exhaustMap(({ id, update }) => [
        ContactsActions.addToProcessingID({ id }),
        ContactsActions.setIsSaving({ isSaving: true }),
        ContactsActions.updateContact({ id, update }),
      ]),
      share(),
    ),
  { functional: true },
);

export const updateContact$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactsActions.updateContact),
      mergeMap(({ id, update }) =>
        service.update(id, update).pipe(
          switchMap(() => [
            ContactsActions.updateContactSuccess({ id, update }),
            ContactsActions.removeFromProcessingID({ id }),
            ContactsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_UPDATED') as string,
            }),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactsActions.removeFromProcessingID({ id }),
            ContactsActions.setIsSaving({ isSaving: false }),
            ContactsActions.updateContactFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startDeleteContact$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactsActions.startDeleteContact),
      exhaustMap(({ id }) => [
        ContactsActions.addToProcessingID({ id }),
        ContactsActions.deleteContact({ id }),
      ]),
      share(),
    ),
  { functional: true },
);

export const deleteContact$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactsService),
    translate = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ContactsActions.deleteContact),
      mergeMap(({ id }) =>
        service.delete(id).pipe(
          switchMap(() => [
            ContactsActions.deleteContactSuccess({ id }),
            ContactsActions.removeFromProcessingID({ id }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_DELETED') as string,
            }),
            ContactsActions.loadMemberStats(),
          ]),
          catchError((response: HttpErrorResponse) => [
            SharedActions.showMessage({ error: errMessage(response) }),
            ContactsActions.removeFromProcessingID({ id }),
            ContactsActions.deleteContactFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);
